import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fmtDate } from "@/lib/format";
import { TXN_BADGE } from "@/lib/constants";
import { Search, Pencil, AlertCircle } from "lucide-react";

function Row({ r, onEdit }) {
    return (
        <tr className="border-t border-border/50" data-testid={`position-row-${r.id}`}>
            <td className="py-1.5 pr-3 num">{fmtDate(r.transaction_date)}</td>
            <td className="pr-3"><Badge variant="outline" className={`text-[10px] ${TXN_BADGE[r.transaction_type] || ""}`}>{r.transaction_type}</Badge></td>
            <td className="pr-3">{r.sub_portfolio}</td>
            <td className="pr-3 num text-right">
                {r.split_adjusted ? <span title={`Entered as ${r.quantity}, split-adjusted`}>{r.quantity} → {+r.adj_quantity.toFixed(4)}</span> : r.quantity}
            </td>
            <td className="pr-3 num text-right">{r.price}</td>
            <td className="pr-3 num text-right">{r.transaction_type === "DIVIDEND" ? "" : +r.running.toFixed(4)}</td>
            <td className="pr-3 text-loss text-[11px]">{r.flag && <span className="inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" />{r.flag}</span>}</td>
            <td className="text-right">
                <button onClick={() => onEdit(r)} className="text-muted-foreground hover:text-primary" data-testid={`position-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></button>
            </td>
        </tr>
    );
}

export function PositionCheck({ ticker, onEdit }) {
    const [input, setInput] = useState(ticker || "");
    const [tk, setTk] = useState(ticker || "");
    useEffect(() => { if (ticker) { setInput(ticker); setTk(ticker); } }, [ticker]);
    const { data, isFetching } = useQuery({
        queryKey: ["position-check", tk],
        queryFn: async () => (await api.get(`/issues/position/${encodeURIComponent(tk)}`)).data,
        enabled: !!tk,
    });
    const rows = (data?.rows || []).filter((r) => r.transaction_type !== "DIVIDEND");
    return (
        <Card className="p-4 space-y-3" id="position-check" data-testid="position-check">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-heading text-base font-semibold">Position check</h2>
                    <p className="text-xs text-muted-foreground">Every buy and sell for one ticker, with the running share count per sub-portfolio after splits.</p>
                </div>
                <form className="flex gap-1.5" onSubmit={(e) => { e.preventDefault(); setTk(input.trim().toUpperCase()); }}>
                    <Input value={input} onChange={(e) => setInput(e.target.value.toUpperCase())} placeholder="Ticker, e.g. BEP" className="h-8 w-[160px] text-xs num uppercase" data-testid="position-check-input" />
                    <Button size="sm" className="h-8 text-xs gap-1" type="submit" data-testid="position-check-btn"><Search className="w-3 h-3" />Check</Button>
                </form>
            </div>
            {tk && data && (
                <>
                    <div className="flex gap-2 flex-wrap text-xs" data-testid="position-check-summary">
                        <span className="text-muted-foreground">Still held:</span>
                        {data.subs.length ? data.subs.map((s) => <Badge key={s.sub} variant="secondary" className="num">{s.sub}: {s.shares} sh</Badge>) : <span>none (closed)</span>}
                        {data.splits.length > 0 && <span className="text-muted-foreground ml-2">Splits: {data.splits.map((s) => s.ignored ? `${s.yahoo_ratio}× on ${fmtDate(s.date)} (spin-off, not counted)` : `${s.ratio}× on ${fmtDate(s.date)}${s.source === "manual" ? " (manual)" : ""}`).join(", ")}</span>}
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="text-muted-foreground text-[10px] uppercase tracking-wider">
                                <tr><th className="text-left py-1">Date</th><th className="text-left">Type</th><th className="text-left">Sub-portfolio</th><th className="text-right pr-3">Qty</th><th className="text-right pr-3">Price</th><th className="text-right pr-3">Held after</th><th></th><th></th></tr>
                            </thead>
                            <tbody>{rows.map((r) => <Row key={r.id} r={r} onEdit={onEdit} />)}</tbody>
                        </table>
                        {!rows.length && !isFetching && <p className="text-xs text-muted-foreground py-3">No buys or sells for {tk}.</p>}
                    </div>
                </>
            )}
        </Card>
    );
}
